import { useMemo, useState } from "react";
import { InputGroup, Tab, Tabs } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import Podium from "./Podium";
import LadderStats from "./LadderStats";
import PlayerTable from "./tables/PlayerTable";
import { useLadderStats, usePlayersByLadder } from "../../hooks/queries/usePlayer";
import { useIsMobile } from "../../hooks/useIsMobile";

interface LadderStandingsProps {
  ladderId: number;
}

/**
 * Shared standings layout: podium + searchable player table on the left, ladder statistics
 * on the right (mobile: two tabs). Used by both the official ladders page (Players.tsx) and
 * the custom-ladder detail page (LadderDetail.tsx) so both surfaces stay in visual lockstep.
 */
function LadderStandings({ ladderId }: LadderStandingsProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [filter, setFilter] = useState("");
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const { data: players = [] } = usePlayersByLadder(ladderId);
  const { data: ladderStats = null } = useLadderStats(ladderId, selectedYear);

  const filteredPlayers = useMemo(
    () => players.filter((p) => p.filter?.includes(filter.toUpperCase()) ?? true),
    [players, filter],
  );

  const topThreePlayers = players.slice(0, 3);

  const rankingSection = (
    <div className="players-ranking-section">
      <Podium players={topThreePlayers} />
      <div className="players-search-bar">
        <InputGroup>
          <InputGroup.Text id="searchbar">{t("search")}</InputGroup.Text>
          <Form.Control value={filter} onChange={(e) => setFilter(e.target.value)} />
        </InputGroup>
      </div>
      <PlayerTable players={filteredPlayers} showRank={true} />
    </div>
  );

  const statsSection = (
    <div className="players-stats-section">
      <LadderStats
        key={ladderId}
        stats={ladderStats}
        selectedYear={selectedYear}
        onYearChange={setSelectedYear}
      />
    </div>
  );

  if (isMobile) {
    return (
      <Tabs defaultActiveKey="ranking" id="players-tabs" variant="tabs" fill className="players-mobile-tabs">
        <Tab eventKey="ranking" title={t("ranking")}>
          <div className="mt-3">{rankingSection}</div>
        </Tab>
        <Tab eventKey="statistics" title={t("statistics")}>
          <div className="mt-3">{statsSection}</div>
        </Tab>
      </Tabs>
    );
  }

  return (
    <div className="players-desktop-layout">
      <div className="players-left-section">{rankingSection}</div>
      <div className="players-right-section">{statsSection}</div>
    </div>
  );
}

export default LadderStandings;
